import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { User, Phone, Mail, Camera, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';

const CustomerProfile = () => {
    const { user, updateProfile } = useUser();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        username: user?.username || '',
        email: user?.email || '',
        phone: user?.phone || '',
    });

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.email || '',
                phone: user.phone || '',
            });
        }
    }, [user]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const success = await updateProfile({ username: formData.username, phone: formData.phone });
        if (success) {
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.email || '',
                phone: user.phone || '',
            });
        }
    };

    return (
        <div className="customer-profile">
            {/* Header */}
            <div className="profile-header">
                <div>
                    <h2>My Profile</h2>
                    <p>Manage your account details</p>
                </div>
                {!isEditing && (
                    <button onClick={() => setIsEditing(true)} className="edit-btn">
                        Edit Profile
                    </button>
                )}
            </div>

            {/* Profile Card */}
            <div className="profile-card">
                <div className="avatar-section">
                    <div className="avatar">
                        {formData.username?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="avatar-info">
                        <h3>{formData.username || 'Customer'}</h3>
                        <span className="badge">Verified Customer</span>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="profile-form">
                    <div className="form-group">
                        <label>Full Name</label>
                        <div className="input-wrapper">
                            <User size={18} />
                            <input 
                                type="text" 
                                name="username"
                                readOnly={!isEditing} 
                                value={formData.username} 
                                onChange={handleInputChange}
                                placeholder="Enter your name"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Email Address</label>
                        <div className="input-wrapper">
                            <Mail size={18} />
                            <input 
                                type="email" 
                                name="email"
                                readOnly={!isEditing} 
                                value={formData.email} 
                                onChange={handleInputChange}
                                placeholder="Enter your email"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Phone Number</label>
                        <div className="input-wrapper">
                            <Phone size={18} />
                            <input 
                                type="tel" 
                                name="phone"
                                readOnly={!isEditing} 
                                value={formData.phone} 
                                onChange={handleInputChange}
                                placeholder="Enter phone number"
                            />
                        </div>
                    </div>

                    {isEditing && (
                        <div className="form-actions">
                            <button type="submit" className="save-btn">
                                <Save size={16} /> Save Changes
                            </button>
                            <button type="button" onClick={handleCancel} className="cancel-btn">
                                <X size={16} /> Cancel
                            </button>
                        </div>
                    )}
                </form>
            </div>

            <style>{`
                .customer-profile {
                    max-width: 100%;
                }

                /* Header */
                .profile-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .profile-header h2 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 4px;
                }

                .profile-header p {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0;
                }

                .edit-btn {
                    padding: 8px 20px;
                    background: white;
                    border: 1px solid #10b981;
                    border-radius: 40px;
                    color: #10b981;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .edit-btn:hover {
                    background: #10b981;
                    color: white;
                }

                /* Profile Card */
                .profile-card {
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e8ecef;
                    overflow: hidden;
                }

                .avatar-section {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    padding: 24px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e8ecef;
                }

                .avatar {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    font-weight: 600;
                    color: white;
                }

                .avatar-info h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 8px;
                }

                .badge {
                    padding: 4px 12px;
                    background: #d1fae5;
                    color: #10b981;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 500;
                }

                /* Form */
                .profile-form {
                    padding: 24px;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    font-size: 13px;
                    font-weight: 500;
                    color: #1e293b;
                    margin-bottom: 8px;
                }

                .input-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    background: white;
                    transition: all 0.2s;
                }

                .input-wrapper:focus-within {
                    border-color: #10b981;
                    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
                }

                .input-wrapper svg {
                    color: #94a3b8;
                    flex-shrink: 0;
                }

                .input-wrapper input {
                    flex: 1;
                    border: none;
                    outline: none;
                    font-size: 14px;
                    color: #1e293b;
                    background: transparent;
                }

                .input-wrapper input::placeholder {
                    color: #cbd5e1;
                }

                .input-wrapper input:read-only {
                    color: #64748b;
                }

                /* Form Actions */
                .form-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 24px;
                    padding-top: 20px;
                    border-top: 1px solid #e8ecef;
                }

                .save-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 24px;
                    background: #10b981;
                    border: none;
                    border-radius: 40px;
                    color: white;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .save-btn:hover {
                    background: #059669;
                }

                .cancel-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 24px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 40px;
                    color: #64748b;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .cancel-btn:hover {
                    border-color: #ef4444;
                    color: #ef4444;
                }

                /* Responsive */
                @media (max-width: 600px) {
                    .avatar-section {
                        flex-direction: column;
                        text-align: center;
                    }
                    
                    .form-actions {
                        flex-direction: column;
                    }
                    
                    .save-btn, .cancel-btn {
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default CustomerProfile;